'use strict';

const _ = require('underscore');
const i18n = require('i18next');
const Backbone = require('backbone');
const Snackbar = require('common/util/snackbar');
const Actions = require('common/domain/action/index');
const Dispatcher = require('common/domain/repository/dispatcher');
const Route = require('common/route');
const DiagramRepository = require('system/domain/repository/diagram');
const ElementRepository = require('system/domain/repository/element');
const SyncState = require('system/domain/entity/sync-state');
const ElementAPI = require('system/infra/api/element');
const CommentCreateService = require('communication/app/service/comment/create');

const ElementActionCreator = Backbone.Model.extend({
    initialize: function() {
        this._fetched = {};
    },

    _contextToKey: function(context) {
        return context.projectId + ':' + context.diagramId + ':'
            + context.dt;
    },

    url: function(context, id) {
        let url = Route.urlFor('api:elements', {
            // 実際は branch に依存
            pid: context.projectId,
            did: context.diagramId
        });
        if(id) {
            url += '/' + id;
        }
        if(context.dt) {
            url += '?dt=' + context.dt;
        }
        return url;
    },

    linkUrl: function(context, id) {
        return Route.urlFor('api:relation', {
            // 実際は branch に依存
            pid: context.projectId,
            eid: id
        });
    },

    fetchElements: function(params) {
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;
        const dt = params.context.dt;
        return new Promise((resolve, reject) => {
            // クエリパラメータで records, relations のどちらor両方を返すか指定できるようにする
            ElementAPI.getElements(projectID, diagramID, dt)
                .then(elements => {
                    const context = {
                        projectId: projectID,
                        diagramId: diagramID,
                        dt: dt
                    };
                    Dispatcher.dispatch({
                        type: Actions.CREATE_NODE,
                        context: context,
                        node: elements.nodes,
                        options: {fetched: true}
                    });
                    Dispatcher.dispatch({
                        type: Actions.CREATE_LINK,
                        context: context,
                        link: elements.links,
                        options: {fetched: true}
                    });
                    resolve(elements);
                }, reject);
        });
    },

    fetchElementsOnce: function(params) {
        let key = this._contextToKey(params.context);
        let deferred = this._fetched[key];
        if(!deferred) {
            deferred = this.fetchElements(params);
            this._fetched[key] = deferred;
        }
        return deferred;
    },

    fetchRelationsByPartial: function(params, relationIDs) {
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;
        const dt = params.context.dt;

        const chunkedRelationIDs= _.chunk(relationIDs, 20);
        const promises = _.map(chunkedRelationIDs, (partialRelationIDs) => {
            return new Promise((resolve, reject) => {
                ElementAPI.getRelationsByPartial(projectID, dt, partialRelationIDs)
                    .then(relations=> {
                        const context = {
                            projectId: projectID,
                            diagramId: diagramID,
                            dt: dt
                        };
                        Dispatcher.dispatch({
                            type: Actions.CREATE_LINK,
                            context: context,
                            link: relations,
                            options: {fetched: true}
                        });
                        resolve(relations);
                    }, reject);
            });
        });

        return Promise.all(promises);
    },

    fetchRelationsByPartialOnce: function(params, relationIDs) {
        let key = this._contextToKey(params.context) + ':' + 'relations';
        let deferred = this._fetched[key];
        if (!deferred) {
            deferred = this.fetchRelationsByPartial(params, relationIDs);
            this._fetched[key] = deferred;
        }
        return deferred;
    },

    fetchRecordsByPartial: function(params, recordIDs) {
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;
        const dt = params.context.dt;

        const chunkedRecordIDs = _.chunk(recordIDs, 20);
        const promises = _.map(chunkedRecordIDs, (partialRecordIDs) => {
            return new Promise((resolve, reject) => {
                ElementAPI.getRecordsByPartial(projectID, dt, partialRecordIDs)
                    .then(records => {
                        const context = {
                            projectId: projectID,
                            diagramId: diagramID,
                            dt: dt
                        };
                        Dispatcher.dispatch({
                            type: Actions.CREATE_NODE,
                            context: context,
                            node: records,
                            options: {fetched: true}
                        });
                        resolve(records);
                    }, reject);
            });
        });

        return Promise.all(promises);
    },

    fetchRecordsByPartialOnce: function(params, recordIDs) {
        let key = this._contextToKey(params.context) + ':' + 'records';
        let deferred = this._fetched[key];
        if (!deferred) {
            deferred = this.fetchRecordsByPartial(params, recordIDs);
            this._fetched[key] = deferred;
        }
        return deferred;
    },

    fetchValueTypes: function(params) {
        const projectID = params.context.projectId;
        const dt = params.context.dt;
        return new Promise((resolve, reject) => {
            ElementAPI.getValueTypes(projectID, dt, params.types)
                .then(valueTypes => {
                    const context = {
                        projectId: projectID,
                        dt: dt
                    };
                    Dispatcher.dispatch({
                        type: Actions.CREATE_NODE,
                        context: context,
                        node: valueTypes
                    });
                    resolve();
                }, reject);
        });
    },

    mergeElements: function(params) {
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;
        return new Promise((resolve, reject) => {
            ElementAPI.getElements(projectID, diagramID)
                .then(elements => {
                    const context = params.context;
                    Dispatcher.dispatch({
                        type: Actions.MERGE_NODE,
                        context: context,
                        node: elements.nodes
                    });
                    Dispatcher.dispatch({
                        type: Actions.MERGE_LINK,
                        context: context,
                        link: elements.links
                    });
                    resolve();
                }, reject);
        });
    },

    fetchRemoteLinks: function(params) {
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;
        const dt = params.context.dt;
        return new Promise((resolve, reject) => {
            ElementAPI.getRemoteLinks(projectID, diagramID, dt)
                .then(elements => {
                    const context = {
                        projectId: projectID,
                        diagramId: diagramID,
                        dt: dt
                    };
                    Dispatcher.dispatch({
                        type: Actions.CREATE_NODE,
                        context: context,
                        node: elements.nodes,
                        options: {fetched: true}
                    });
                    Dispatcher.dispatch({
                        type: Actions.CREATE_LINK,
                        context: context,
                        link: elements.links,
                        options: {fetched: true}
                    });
                    resolve();
                }, reject);
        });
    },

    createNode: function(params) {
        params.nodeMeta.creating = true;
        params.node.creating = true;

        // meta 情報を作成
        Dispatcher.dispatch({
            type: Actions.CREATE_NODE_META,
            context: params.context,
            nodeMeta: params.nodeMeta
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            nodeMeta: params.nodeMeta
        });

        // model を作成
        Dispatcher.dispatch({
            type: Actions.CREATE_NODE,
            context: params.context,
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: params.node
        });
    },

    createRemoteNode: function(params) {
        params.node.creating = true;
        Dispatcher.dispatch({
            type: Actions.CREATE_NODE,
            context: params.context,
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: params.node
        });
    },

    createSymbolicNode: function(params) {
        params.nodeMeta.creating = true;
        // 既に model 自体は存在してる
        params.node.creating = false;

        // meta 情報を作成
        Dispatcher.dispatch({
            type: Actions.CREATE_NODE_META,
            context: params.context,
            nodeMeta: params.nodeMeta
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            nodeMeta: params.nodeMeta
        });

        // model を作成
        Dispatcher.dispatch({
            type: Actions.CREATE_NODE,
            context: params.context,
            node: params.node
        });
    },

    updateNode: function(params) {
        if(params.nodeMeta) {
            Dispatcher.dispatch({
                type: Actions.UPDATE_NODE_META,
                context: params.context,
                nodeMeta: params.nodeMeta
            });
            Dispatcher.dispatch({
                type: Actions.UPDATE_NODE_META_SYNC_STATE,
                context: params.context,
                state: SyncState.States.unsync,
                nodeMeta: params.nodeMeta
            });
        }
        if(params.node) {
            Dispatcher.dispatch({
                type: Actions.UPDATE_NODE,
                context: params.context,
                node: params.node
            });
            Dispatcher.dispatch({
                type: Actions.UPDATE_NODE_SYNC_STATE,
                context: params.context,
                state: SyncState.States.unsync,
                node: params.node
            });
        }
    },

    deleteNode: function(params) {
        let j = {id: params.node.id, deleting: true};
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META,
            context: params.context,
            nodeMeta: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            nodeMeta: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE,
            context: params.context,
            node: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: j
        });
        let linkMetas = DiagramRepository.getLinkMetas({
            context: params.context
        });
        ElementRepository.getLinks({context: params.context}).chain()
            .filter(function(link) {
                return !link.get('deleted') && linkMetas.get(link.id) &&
                    (link.source.id === params.node.id
                    || link.target.id === params.node.id);
            })
            .each(function(link) {
                this.deleteLink({
                    context: params.context,
                    link: {id: link.id}
                });
            }, this);
    },

    deleteSymbolicNode: function(params) {
        let j = {id: params.node.id, deleting: true};
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META,
            context: params.context,
            nodeMeta: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            nodeMeta: j
        });
    },

    addTagToNode: function(params) {
        Dispatcher.dispatch({
            type: Actions.ADD_TAG_TO_NODE,
            context: params.context,
            tag: params.tag,
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: params.node
        });
    },

    removeTagFromNode: function(params) {
        Dispatcher.dispatch({
            type: Actions.REMOVE_TAG_FROM_NODE,
            context: params.context,
            tag: params.tag,
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: params.node
        });
    },

    addOwnerToNode: function(params) {
        Dispatcher.dispatch({
            type: Actions.ADD_OWNER_TO_NODE,
            context: params.context,
            user: params.user,
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: params.node
        });
    },

    removeOwnerFromNode: function(params) {
        Dispatcher.dispatch({
            type: Actions.REMOVE_OWNER_FROM_NODE,
            context: params.context,
            user: params.user,
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            node: params.node
        });
    },

    resyncNode: function(params) {
        const projectID = params.context.projectId;
        const dt = params.context.dt;
        return new Promise((resolve, reject) => {
            ElementAPI.getNode(projectID, params.node.id, dt)
                .then(node => {
                    const context = {
                        projectId: projectID,
                        dt: dt
                    };
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_NODE,
                        context: context,
                        node: node
                    });
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_NODE_SYNC_STATE,
                        context: context,
                        state: SyncState.States.synced,
                        node: node
                    });
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_NODE_EDIT_STATE,
                        context: context,
                        state: 'end',
                        node: node
                    });
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_NODE_EDIT_STATE,
                        context: context,
                        state: 'start',
                        node: node
                    });
                    resolve();
                }, reject);
        });
    },

    startEditNode: function(params) {
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_EDIT_STATE,
            context: params.context,
            state: 'start',
            nodeMeta: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_EDIT_STATE,
            context: params.context,
            state: 'start',
            node: params.node
        });
    },

    cancelEditNode: function(params) {
        // meta 情報
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_EDIT_STATE,
            context: params.context,
            state: 'cancel',
            nodeMeta: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.synced,
            nodeMeta: params.node
        });

        // model
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_EDIT_STATE,
            context: params.context,
            state: 'cancel',
            node: params.node
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: params.context,
            state: SyncState.States.synced,
            node: params.node
        });
    },

    createLink: function(params) {
        params.linkMeta.creating = true;
        params.link.creating = true;

        // meta 情報を作成
        Dispatcher.dispatch({
            type: Actions.CREATE_LINK_META,
            context: params.context,
            linkMeta: params.linkMeta
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            linkMeta: params.linkMeta
        });

        // model
        Dispatcher.dispatch({
            type: Actions.CREATE_LINK,
            context: params.context,
            link: params.link
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            link: params.link
        });
    },

    createRemoteLink: function(params) {
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;

        params.link.creating = true;
        params.link.deleting = false;
        params.link.remote = true;

        return ElementAPI.updateElements(projectID, diagramID, {
            metas: {relations: {}},
            models: {
                relations: [params.link]
            }
        }).then(data => {
            let j = data.models.relations[0];
            Dispatcher.dispatch({
                type: Actions.CREATE_LINK,
                context: params.context,
                link: j
            });
            Dispatcher.dispatch({
                type: Actions.UPDATE_LINK_SYNC_STATE,
                context: params.context,
                state: SyncState.States.synced,
                link: j
            });
        });
    },

    updateRemoteLink: function(params) {
        const projectID = params.context.projectId;
        return ElementAPI.updateLink(projectID, params.link.id, params.link)
            .then(data => {
                const context = {
                    projectId: projectID
                };
                Dispatcher.dispatch({
                    type: Actions.UPDATE_LINK,
                    context: context,
                    link: data
                });
            })
    },

    deleteRemoteLink: function(params) {
        const projectID = params.context.projectId;
        params.link.creating = false;
        params.link.deleting = true;

        return ElementAPI.updateLink(projectID, params.link.id, {
            metas: {relations: {}},
            models: {
                relations: [params.link]
            }
        }).then(() => {
            const context = {
                projectId: projectID
            };
            Dispatcher.dispatch({
                type: Actions.DELETE_LINK,
                context: context,
                linkJSON: params.link
            });
        });
    },


    updateLink: function(params) {
        if(params.linkMeta) {
            Dispatcher.dispatch({
                type: Actions.UPDATE_LINK_META,
                context: params.context,
                linkMeta: params.linkMeta
            });
            Dispatcher.dispatch({
                type: Actions.UPDATE_LINK_META_SYNC_STATE,
                context: params.context,
                state: SyncState.States.unsync,
                linkMeta: params.linkMeta
            });
        }
        if(params.link) {
            Dispatcher.dispatch({
                type: Actions.UPDATE_LINK,
                context: params.context,
                link: params.link
            });
            Dispatcher.dispatch({
                type: Actions.UPDATE_LINK_SYNC_STATE,
                context: params.context,
                state: SyncState.States.unsync,
                link: params.link
            });
        }
    },

    deleteLink: function(params) {
        let j = {id: params.link.id, deleting: true};
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META,
            context: params.context,
            linkMeta: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            linkMeta: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK,
            context: params.context,
            link: j
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_SYNC_STATE,
            context: params.context,
            state: SyncState.States.unsync,
            link: j
        });
    },

    resyncLink: function(params) {
        const projectID = params.context.projectId;
        const dt = params.context.dt;
        return new Promise((resolve, reject) => {
            ElementAPI.getLink(projectID, params.link.id, dt)
                .then(link => {
                    const context = {
                        projectId: projectID,
                        dt: dt
                    };
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_LINK,
                        context: context,
                        link: link
                    });
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_LINK_SYNC_STATE,
                        context: context,
                        state: SyncState.States.synced,
                        link: link
                    });
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_LINK_EDIT_STATE,
                        context: context,
                        state: 'end',
                        link: link
                    });
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_LINK_EDIT_STATE,
                        context: context,
                        state: 'start',
                        link: link
                    });
                    resolve();
                }, reject);
        });
    },

    invertLink: function(params) {
        let diagram = DiagramRepository.getDiagrams({
            context: params.context
        }).get(params.context.diagramId);
        _.each(params.link, function(link) {
            let meta = diagram.getLinkMeta(link.id);
            let vendAt = meta.get('vendAt');
            if(vendAt) {
                vendAt.r = 1 - vendAt.r;
                vendAt.d = - vendAt.d;
            }
            this.updateLink({
                context: params.context,
                link: {
                    id: link.id,
                    source: link.target,
                    target: link.source
                },
                linkMeta: {
                    id: link.id,
                    vendAt: vendAt
                }
            });
        }, this);
    },

    startEditLink: function(params) {
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_EDIT_STATE,
            context: params.context,
            state: 'start',
            linkMeta: params.link
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_EDIT_STATE,
            context: params.context,
            state: 'start',
            link: params.link
        });
    },

    cancelEditLink: function(params) {
        // meta 情報
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_EDIT_STATE,
            context: params.context,
            state: 'cancel',
            linkMeta: params.link
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_SYNC_STATE,
            context: params.context,
            state: SyncState.States.synced,
            linkMeta: params.link
        });

        // model
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_EDIT_STATE,
            context: params.context,
            state: 'cancel',
            link: params.link
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_SYNC_STATE,
            context: params.context,
            state: SyncState.States.synced,
            link: params.link
        });
    },

    /**
     * params は以下の構造
     * params = {
         *     context: {},
         *     metas: {
         *         nodes: {},
         *         links: {}
         *     },
         *     models: {
         *         nodes: [],
         *         links: []
         *     },
         *     commitComment: {}
         * }
     */
    saveElements: function(params) {
        let metas = _.extend({nodes: {}, links: {}}, params.metas);
        let models = _.extend({nodes: [], links: []}, params.models);

        let j = {
            metas: {
                records: metas.nodes,
                relations: metas.links
            },
            models: {
                records: models.nodes,
                relations: models.links
            }
        };

        // SyncState を変更
        let state = SyncState.States.syncing;
        this._setStates(params.context, models, metas, state);

        // Sync
        const projectID = params.context.projectId;
        const diagramID = params.context.diagramId;
        const promise = ElementAPI.updateElements(
            projectID, diagramID, j
        ).then(
            _.bind(this.handleSuccess,
                this, params.context, models, metas),
            _.bind(this.handleError,
                this, params.context, models, metas)
        ).then(() => {
            if (params.commitComment && params.commitComment.comment) {
                const project = params.commitComment.project;
                const comment = params.commitComment.comment;
                (new CommentCreateService(project, comment)).execute();
            }
        });
        return promise;
    },

    handleSuccess: function(context, models, metas, data) {
        let nodes = _.map(data.models.records, function(node) {
            node.creating = false;
            node.deleting = false;
            return node;
        });
        let links = _.map(data.models.relations, function(link) {
            link.creating = false;
            link.deleting = false;
            return link;
        });
        let deletedLinks = _.filter(data.models.relations, function(link) {
            return link.deleted;
        });
        _.each(deletedLinks, function(link) {
            models.links.push(link);
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_DIAGRAM,
            context: context,
            diagram: data.dataset
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE,
            context: context,
            node: nodes
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK,
            context: context,
            link: links
        });

        let nodeMetas = [];
        let deletedNodeMetaIds = [];
        _.each(metas.nodes, function(node, id) {
            node.id = id;
            node.creating = false;
            if(node.deleting) {
                deletedNodeMetaIds.push(id);
            } else {
                nodeMetas.push(node);
            }
        });
        let linkMetas = [];
        let deletedLinkMetaIds = _.pluck(deletedLinks, 'id');
        _.each(metas.links, function(link, id) {
            link.id = id;
            link.creating = false;
            if(link.deleting) {
                deletedLinkMetaIds.push(id);
            } else {
                linkMetas.push(link);
            }
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META,
            context: context,
            nodeMeta: nodeMetas
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META,
            context: context,
            linkMeta: linkMetas
        });

        let editNodeIds = _.union(_.pluck(models.nodes, 'id'),
            _.pluck(nodeMetas, 'id'));
        let editLinkIds = _.union(_.pluck(models.links, 'id'),
            _.pluck(linkMetas, 'id'));
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_EDIT_STATE,
            context: context,
            state: 'end',
            node: _.map(editNodeIds, function(i){return {id:i}})
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_EDIT_STATE,
            context: context,
            state: 'end',
            link: _.map(editLinkIds, function(i){return {id:i}})
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_EDIT_STATE,
            context: context,
            state: 'end',
            nodeMeta: _.map(editNodeIds, function(i){return {id:i}})
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_EDIT_STATE,
            context: context,
            state: 'end',
            linkMeta: _.map(editLinkIds, function(i){return {id:i}})
        });

        let state = SyncState.States.synced;
        this._setStates(context, models, metas, state);

        Dispatcher.dispatch({
            type: Actions.DELETE_NODE_META,
            context: context,
            nodeMeta: _.map(deletedNodeMetaIds,
                function(i){return {id:i}})
        });
        Dispatcher.dispatch({
            type: Actions.DELETE_LINK_META,
            context: context,
            linkMeta: _.map(deletedLinkMetaIds,
                function(i){return {id:i}})
        });

        Snackbar.success({title: i18n.t('notify.saved')});
    },

    handleError: function(context, models, metas, jqXHR) {
        let state = SyncState.States.unsync;
        if(jqXHR.status === 409) {
            this._setStates(context, models, metas, state);

            let conflicted = jqXHR.responseJSON.data.conflicted;
            state = SyncState.States.conflict;
            Dispatcher.dispatch({
                type: Actions.UPDATE_NODE_SYNC_STATE,
                context: context,
                state: state,
                node: _.map(conflicted.records, function(id) {
                    return {id: id};
                })
            });
            Dispatcher.dispatch({
                type: Actions.UPDATE_LINK_SYNC_STATE,
                context: context,
                state: state,
                link: _.map(conflicted.relations, function(id) {
                    return {id: id};
                })
            });
        } else {
            this._setStates(context, models, metas, state);
        }
    },

    _setStates: function(context, models, metas, state) {
        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_META_SYNC_STATE,
            context: context,
            state: state,
            nodeMeta: _.map(_.keys(metas.nodes), function(i) {
                return {id: i};
            })
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_META_SYNC_STATE,
            context: context,
            state: state,
            linkMeta: _.map(_.keys(metas.links), function(i) {
                return {id: i};
            })
        });

        Dispatcher.dispatch({
            type: Actions.UPDATE_NODE_SYNC_STATE,
            context: context,
            state: state,
            node: models.nodes
        });
        Dispatcher.dispatch({
            type: Actions.UPDATE_LINK_SYNC_STATE,
            context: context,
            state: state,
            link: models.links
        });
    }
});

module.exports = new ElementActionCreator();



// WEBPACK FOOTER //
// ./system/domain/action/element.js